import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../store/context";
import { IOperationsStatus, ModalityType, Operations } from "../../types/FaceVerification";
import { addQrSession, addSession, showMessage, showOperationSummary } from "../../store/reducer";
import { Grid } from "@mui/material";
import React from "react";
import { RefVideoStream } from "../VideoStream/BiometricFaceStream";
import { useHistory } from "react-router-dom";
import OperationContainer from "../../components/Operations/styled/OperationContainer";
import OperationTitle from "../../components/Operations/styled/OperationTitle";
import FaceOperation from "../Enroll/Modalities/FaceOperation";
import VoiceOperation from "../Enroll/Modalities/VoiceOperation";
import Overview from "../Overview/Overview";
import VerifyFromQrCodeSideBar from "./components/VerifyFromQrCodeSideBar";
import QrStreamOperation from "../Enroll/Modalities/QrStreamOperation";
import { NavigationContext } from "../../components/Navigation/store/context";
import { getInitParamsByManagementConfig, getManagementInfo, useOperationStateEffect } from "../../helpers/operationHelper";
import { APIsContext } from "../../store/APIs/context";
import { FVInitParams } from "neurotec-megamatcherid-management-client";
import { startSession } from "../../helpers/webRTC";
import { initialFvParameters, useLocalStorage } from "../../helpers/useLocalStorage";
import { mmidInitParamsStorage } from "../../config/const";
import { useSnackbar } from "notistack";

const VerifyFromQrCode: React.FC = () => {

    const apisState = useContext(APIsContext).state
    const [options] = useLocalStorage<FVInitParams>(mmidInitParamsStorage, initialFvParameters);
    const { state, dispatch } = useContext(AppContext)
    const childRef = React.useRef<RefVideoStream>(null)
    const [checkState, setCheckState] = useState<IOperationsStatus>(IOperationsStatus.IDLE);
    const history = useHistory()
    const [faceOperationUUID, setFaceOperationUUID] = useState("");
    const [voiceOperationUUID, setVoiceOperationUUID] = useState("");
    const [activeModality, setActiveModality] = useState<ModalityType | null>(null);
    const navigationDispatch = useContext(NavigationContext).dispatch
    const [liveAdjudication, setLiveAdjudication] = useState(false)
    const { enqueueSnackbar } = useSnackbar();

    useOperationStateEffect(
        state,
        apisState,
        dispatch,
        checkState,
        (status: IOperationsStatus) => setCheckState(status),
        activeModality,
        (modality: ModalityType | null) => setActiveModality(modality),
        navigationDispatch,
        liveAdjudication,
        enqueueSnackbar,
        faceOperationUUID,
        childRef.current?.cancel,
    )

    useEffect(() => {
        if (state.activeModalities.qrScan) {
            setActiveModality(prev => {
                if (prev == null) {
                    return ModalityType.QR_SCAN
                } else {
                    return prev
                }
            })
        }
        if (state.activeModalities.faceModality) {
            setActiveModality(prev => {
                if (prev == null || ModalityType.QR_SCAN) {
                    return ModalityType.FACE_MODALITY
                } else {
                    return prev
                }
            })
        }
        if (state.activeModalities.voiceModality) {
            setActiveModality(prev => {
                if (prev == null || ModalityType.QR_SCAN) {
                    return ModalityType.VOICE_MODALITY
                } else {
                    return prev
                }
            })
        }
    }, [state.activeModalities.faceModality,state.activeModalities.voiceModality, history, state.activeModalities.qrScan])

    const restart = () => {
        dispatch(showOperationSummary(""));
        setCheckState(IOperationsStatus.IDLE)
    }

    const verifyFromTemplate = async () => {
        if (!state.options?.verificationTemplate)
            return;
        getManagementInfo(apisState.ManagementApi).then(res => {
            if (res === null) {
                enqueueSnackbar("Could not connect to the server", {variant: "error"})
                return;
            }
            setLiveAdjudication(res.liveAdjudication)
            let initParams: FVInitParams = getInitParamsByManagementConfig(res, options);
            initParams.verificationTemplate = state.options?.verificationTemplate;
            initParams.type = Operations.VERIFY_WITH_QR;
            startSession(apisState.JanusApi, Operations.VERIFY_WITH_QR, initParams).then(res => {
                setCheckState(IOperationsStatus.ACTIVE)
                dispatch(addSession(res.data, initParams));
            }).catch(err => {
                setCheckState(IOperationsStatus.IDLE)
                console.log(err)
                dispatch(showMessage({ message: "Unable to create session: " + err.message, type: "error" }))
            })
        }).catch(e => {
            enqueueSnackbar(e.message, {variant: "error"})
        })
    }

    const startQrScanning = () => {
        dispatch(addQrSession());
    }

    const getName = (): string => {
        switch (activeModality) {
            case ModalityType.QR_SCAN:
                return "Scan QR code"
            case ModalityType.FACE_MODALITY:
                return "Face verification"
            case ModalityType.VOICE_MODALITY:
                return "Voice verification"
            case ModalityType.OVERVIEW:
                return "Overview"
            default: 
                return "Verification"
        }
    }

    const getActiveState = () => {
        switch (activeModality) {
            case ModalityType.QR_SCAN:
                return (
                    <QrStreamOperation
                        setCheckState={setCheckState}
                        childRef={childRef}    
                    />
                )
            case ModalityType.FACE_MODALITY:
                return (
                    <FaceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setFaceOperationUUID}
                    />
                )
            case ModalityType.VOICE_MODALITY:
                return (
                    <VoiceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setVoiceOperationUUID}
                    />
                )
            case ModalityType.OVERVIEW:
                return  (
                    <Overview
                        faceOperationUUID={faceOperationUUID}
                        voiceOperationUUID={voiceOperationUUID}
                    />
                )
        }
    }
    
    return (
        <OperationContainer maxWidth={false}>
            <Grid container>
                <OperationTitle>
                    {getName()}
                </OperationTitle>
                {getActiveState()}
                <VerifyFromQrCodeSideBar
                    activeModality={activeModality}
                    checkState={checkState}
                    verifyFromTemplate={verifyFromTemplate}
                    startQrScanning={startQrScanning}
                    restart={restart}
                    cancel={childRef.current?.cancel}
                />
            </Grid>
        </OperationContainer>
    );
}

export default VerifyFromQrCode
